import React, {useState, useEffect, useMemo} from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from "react-router-dom";
import transactions from '../../assets/transactions.svg';
import whatsapp from '../../assets/whatsapp.svg';
import analytics from '../../assets/analytics.svg';
import clients from '../../assets/clients.svg';
import profile from '../../assets/profile.svg';
// TEMPORARILY HIDDEN: Unused imports for hidden sections (can be restored easily)
// import reviews from '../../assets/reviews.svg';
import security from '../../assets/security.svg';
import ads from '../../assets/ads.svg';
import offers from '../../assets/offers.svg';
import subscriptions from '../../assets/subscriptions.svg';
import cross from '../../assets/cross.svg';
import { Roles } from "../../../../config";
import styles from './Navigation.module.css';
import { getAllowedRoutes } from "../../../../tools/utils";
import { useTranslation } from "react-i18next";
import { useCurrentBranch } from "../../../../hooks/useCurrentBranch";
import { deriveChannelsView, isChannelConnected } from "../../../../pages/WhatsApp/channelHelpers";
import ChatsModal from "../../../ChatsModal";

const arrowSvg = (
  <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 1L6 5L1 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function Navigation() {
    // Restore collapsed state from sessionStorage on initial load
    const getInitialCollapsed = () => {
        const saved = sessionStorage.getItem('nav-collapsed');
        return saved ? parseInt(saved, 10) : 1;
    };
    const [collapsed, setCollapsed] = useState(getInitialCollapsed);
    const [activeIndexFound, setActiveIndexFound] = useState(false);
    const { currentBranch, hasActiveSubscription } = useCurrentBranch();
    const branchInfo = useSelector(s => s.branchReducer?.info);
    const { t } = useTranslation();
    const location = useLocation();

    // Save collapsed state to sessionStorage when it changes
    useEffect(() => {
        sessionStorage.setItem('nav-collapsed', collapsed.toString());
    }, [collapsed]);

    const routes = [
        {
          img: transactions,
          title: t('news'),
          route: '/',
          children: [],
          permission: [
            Roles.ADMINISTRATOR,
            Roles.CASHIER,
          ],
          requiresSubscription: true,
        },
        {
            img: transactions,
            title: t('NSSidebar.transactions'),
            route: '/transactions',
            children: [],
            permission: [
                Roles.ADMINISTRATOR,
                Roles.CASHIER,
            ],
            requiresSubscription: true,
        },
        {
            img: whatsapp,
            title: 'WhatsApp',
            route: '/whatsapp',
            children: [],
            permission: [
                Roles.ADMINISTRATOR,
                Roles.CASHIER,
            ],
            requiresSubscription: true,
            toggleKey: 'whatsapp_sending',
        },
        // TEMPORARILY HIDDEN: WhatsApp chats section
        // {
        //     img: whatsapp,
        //     title: t('NSSidebar.wabaChats'),
        //     route: '/waba-chats',
        //     children: [],
        //     permission: [Roles.ADMINISTRATOR],
        //     requiresSubscription: true,
        //     hiddenToggleKey: 'whatsapp_chats_hidden',
        // },
        {
            img: analytics,
            title: t('NSSidebar.analytics'),
            route: '/analytics',
            children: [
                {
                    img: analytics,
                    title: t('NSSidebar.analysis'),
                    route: '/analytics/rfm',
                    toggleKey: 'rfm_analytics',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.sales'),
                    route: '/analytics',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.businessReport'),
                    route: '/analytics/business-report',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.new'),
                    route: '/analytics/loyal',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.audience'),
                    route: '/analytics/audit',
                },
                {
                    img: analytics,
                    title: t('bonuses'),
                    route: '/analytics/bonuses',
                },
                {
                    img: analytics,
                    title: t('NSTag.tags'),
                    route: '/analytics/tags',
                    toggleKey: 'tags',
                },
                {
                    img: analytics,
                    title: t('reviews'),
                    route: '/analytics/reviews',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.referralAnalytics'),
                    route: '/analytics/referral',
                    toggleKey: 'referrals',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.notificationAnalytics'),
                    route: '/analytics/notifications',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.cashierBonuses'),
                    route: '/analytics/cashier-bonuses',
                },
                {
                    img: analytics,
                    title: t('NSSidebar.qrAnalytics'),
                    route: '/analytics/qr',
                    toggleKey: 'qr_registration',
                },
            ],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
        },
        {
            img: clients,
            title: t('NSSidebar.clients'),
            route: '/clients',
            children: [],
            permission: [
                Roles.ADMINISTRATOR,
                Roles.CASHIER,
            ],
            requiresSubscription: true,
        },
        {
            img: clients,
            title: t('dataOfClients'),
            route: '/clients-database',
            children: [],
            permission: [
                Roles.ADMINISTRATOR,
                Roles.CASHIER,
            ],
            requiresSubscription: true,
        },
        {
            img: clients,
            title: t('NSSidebar.campaigns'),
            route: '/campaigns',
            children: [
                {
                    img: clients,
                    title: t('NSCampaigns.navAnalytics'),
                    route: '/campaigns',
                },
                {
                    img: clients,
                    title: t('NSCampaigns.navNew'),
                    route: '/campaigns/new',
                },
            ],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
            requiresBrandedWaba: true,
        },
        {
            img: profile,
            title: t('NSSidebar.profile'),
            route: '/profile',
            children: [
                {
                    img: profile,
                    title: t('NSSidebar.info'),
                    route: '/profile',
                },
                {
                    img: profile,
                    title: t('certificates'),
                    route: '/profile/certificate/list',
                    toggleKey: 'certificates',
                },
                {
                    img: profile,
                    title: t('Reviews.name'),
                    route: '/profile/review-request',
                    toggleKey: 'whatsapp_review',
                },
                {
                    img: profile,
                    title: t('accessRights'),
                    route: '/profile/access-rights',
                    permission: [
                        Roles.ADMINISTRATOR,
                    ],
                },
                {
                    img: profile,
                    title: t('NSSidebar.changePassword'),
                    route: '/profile/change-password',
                },
                {
                    img: whatsapp,
                    title: t('notificationLang'),
                    route: '/profile/notification-whatsapp',
                    toggleKey: 'whatsapp_sending',
                },
                {
                    img: whatsapp,
                    title: t('conf_whatsapp_code'),
                    route: '/profile/whatsapp/code',
                    toggleKey: 'whatsapp_sending',
                },
                {
                    img: whatsapp,
                    title: t('whatsappHeader'),
                    route: '/profile/whatsapp-header',
                    toggleKey: 'whatsapp_sending',
                },
                {
                    img: profile,
                    title: t('NSSidebar.work'),
                    route: '/profile/schedule',
                },
                {
                    img: profile,
                    title: t('NSSidebar.photo'),
                    route: '/profile/photos',
                },
                {
                    img: profile,
                    title: t('NSSidebar.networks'),
                    route: '/profile/socials',
                },
                {
                    img: profile,
                    title: t('NSSidebar.bonus'),
                    route: '/profile/bonus/term',
                },
                {
                    img: profile,
                    title: t('NSSidebar.awards'),
                    route: '/profile/prizes',
                    toggleKey: 'awards',
                },
                {
                    img: profile,
                    title: t('NSSidebar.analysis'),
                    route: '/profile/rfm',
                    toggleKey: 'rfm_analytics',
                },
                {
                    img: profile,
                    title: t('NSSidebar.poll'),
                    route: '/profile/surveys',
                },
                {
                    img: profile,
                    title: t('NSTag.tags'),
                    route: '/profile/tags',
                    toggleKey: 'tags',
                },
                {
                    img: profile,
                    title: t('NSSidebar.qrRegistration'),
                    route: '/profile/qr-registration',
                    toggleKey: 'qr_registration',
                },
                {
                    img: profile,
                    title: t('notifications'),
                    route: '/profile/notifications',
                    toggleKey: 'mass_notifications',
                },
            ],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
        },
        // TEMPORARILY HIDDEN: Subscriptions section
        // {
        //     img: subscriptions,
        //     title: t('NSSidebar.subscriptions'),
        //     route: '/subscriptions',
        //     children: [
        //         {
        //             img: subscriptions,
        //             title: 'Активность',
        //             route: '/subscriptions',
        //         },
        //         {
        //             img: subscriptions,
        //             title: 'Настройки',
        //             route: '/subscriptions/settings',
        //         },
        //         {
        //             img: subscriptions,
        //             title: t('NSSidebar.subscriptionsAnalytics'),
        //             route: '/subscriptions/analytics',
        //         },
        //     ],
        //     permission: [
        //         Roles.ADMINISTRATOR,
        //     ],
        //     alwaysShow: true,
        // },
        // TEMPORARILY HIDDEN: Reviews section (can be restored easily)
        // {
        //     img: reviews,
        //     title: t('NSSidebar.reviews'),
        //     route: '/reviews',
        //     children: [],
        //     permission: [
        //         Roles.ADMINISTRATOR,
        //         Roles.CASHIER,
        //     ],
        //     requiresSubscription: true,
        // },
        {
            img: ads,
            title: t('NSSidebar.ads'),
            route: '/ads',
            children: [
                {
                    img: null,
                    title: t('NSSidebar.info'),
                    route: '/ads',
                },
            ],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
            toggleKey: 'advertisements',
        },
        // TEMPORARILY HIDDEN: Offers section (can be restored easily)
        // {
        //     img: offers,
        //     title: t('NSSidebar.offers'),
        //     route: '/offers',
        //     children: [],
        //     permission: [
        //         Roles.ADMINISTRATOR,
        //     ],
        //     alwaysShow: true,
        // },
        // TEMPORARILY HIDDEN: Business Info (/info) section
        // {
        //     img: profile,
        //     title: t('NSSidebar.businessInfo'),
        //     route: '/info',
        //     children: [],
        //     permission: [
        //         Roles.ADMINISTRATOR,
        //     ],
        //     alwaysShow: true,
        // },
        {
            img: cross,
            title: t('NSSidebar.cross'),
            route: '/cross-marketing',
            children: [
                {
                    img: null,
                    title: t('NSSidebar.administration'),
                    route: '/cross-marketing',
                },
                {
                    img: null,
                    title: t('NSSidebar.analytics1'),
                    route: '/cross-marketing/analytics',
                },
            ],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
            toggleKey: 'cross_marketing',
        },
        {
            img: security,
            title: t('NSSidebar.security'),
            route: '/security',
            children: [],
            permission: [
                Roles.ADMINISTRATOR,
            ],
            requiresSubscription: true,
            toggleKey: 'security',
        },
    ]

    const accessToggles = branchInfo?.configuration?.access_toggles;

    const brandedWabaConnected = isChannelConnected(deriveChannelsView(branchInfo).channel_1);

    const isToggleEnabled = (toggleKey) => {
        if (!toggleKey) return true;
        const toggle = accessToggles?.[toggleKey];
        // If toggle doesn't exist yet (undefined), allow access for backward compatibility
        if (toggle === undefined) return true;
        return toggle?.enabled;
    };

    // Inverted toggles (e.g. whatsapp_chats_hidden): the feature is on by default
    // and enabling the toggle in the admin panel hides it.
    const isNotHidden = (hiddenToggleKey) => {
        if (!hiddenToggleKey) return true;
        return !accessToggles?.[hiddenToggleKey]?.enabled;
    };

    const allowedRoutes = useMemo(() => {
        if (currentBranch) {
            const roleFiltered = getAllowedRoutes(routes, [currentBranch.role]);

            return roleFiltered.filter(route => {
                if (route.alwaysShow) return true;
                if (route.requiresSubscription && !hasActiveSubscription) {
                    return false;
                }
                // Filter top-level routes by access toggle
                if (!isToggleEnabled(route.toggleKey)) {
                    return false;
                }
                if (!isNotHidden(route.hiddenToggleKey)) {
                    return false;
                }
                if (route.requiresBrandedWaba && !brandedWabaConnected) {
                    return false;
                }
                return true;
            }).map(route => {
                // Filter children by access toggle
                if (route.children && route.children.length > 0) {
                    const filteredChildren = route.children.filter(child =>
                        isToggleEnabled(child.toggleKey) && isNotHidden(child.hiddenToggleKey)
                    );
                    if (filteredChildren.length !== route.children.length) {
                        return { ...route, children: filteredChildren };
                    }
                }
                return route;
            });
        }
        return []
    }, [currentBranch, hasActiveSubscription, accessToggles, brandedWabaConnected])

    useEffect(() => {
        setActiveIndexFound(false);
    }, [location.pathname]);

    const changeIndex = (indx) => {
        if (collapsed === indx) return setCollapsed(0);
        setCollapsed(indx);
    }

    const renderBtn = (item, index) => {
        if (location.pathname.split('NSSidebar./')[1] === item.route.slice(1) && !activeIndexFound) {
            if (collapsed !== index) {
                setCollapsed(index);
                setActiveIndexFound(true);
            }
        }

        if (item.route === '/') {
            return (
              <NavLink
                to={item.route}
                exact
                activeClassName={index === collapsed ? styles.activeTab : ''}
                className={styles.btn} key={index}
                onClick={() => changeIndex(index)}>
                  <img src={item.img}/>
                  <span>{item.title}</span>
              </NavLink>
            )
        }
        if (item.children.length) {
            return (
              <React.Fragment key={index}>
                  <button
                    to={item.route}
                    className={`${styles.btn} ${index === collapsed ? styles.activeTab : ''}`}
                    onClick={() => changeIndex(index)}
                  >
                      <img src={item.img}/>
                      <span>{item.title}</span>
                      <i>{arrowSvg}</i>
                  </button>
                  <div className={`${styles.wrapper} ${index === collapsed ? styles.collapsed : ''}`}>
                      {
                          item.children.map((child) => (
                            <NavLink to={child.route} exact activeClassName={styles.linkActive} className={styles.link} key={child.title}>
                                <span>{child.title}</span>
                            </NavLink>
                          ))
                      }
                  </div>
              </React.Fragment>
            )
        }

        return (
          <NavLink to={item.route} activeClassName={index === collapsed ? styles.activeTab : ''} className={styles.btn} key={index}
                   onClick={() => changeIndex(index)}>
              <img src={item.img}/>
              <span>{item.title}</span>
          </NavLink>
        )
    }

    return (
        <div className={styles.container}>
            {allowedRoutes.map((item, index) => renderBtn(item, index + 1))}
            {!hasActiveSubscription && (
                <div className={styles.subscriptionNotice}>
                    <p>Для доступа к программе лояльности свяжитесь с отделом продаж</p>
                    <div className={styles.contactInfo}>
                        <span className={styles.phoneNumber}>+7 708 485 71 01</span>
                        <a
                            href="https://wa.me/77084857101"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.whatsappButton}
                            title="Написать в WhatsApp"
                        >
                            <img src={whatsapp} alt="WhatsApp" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    )
}
